import React from 'react';

import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import EditorialBulletList from './EditorialBulletList.component';
import { EditorialJourneyStep } from '../../types/editorial-journey-step.type';

interface EditorialJourneyProps {
  steps: EditorialJourneyStep[];
}

const useStyles = makeStyles((theme) => ({
  journey: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3.5rem',
  },
  // La contrainte porte sur la hauteur, pas sur la largeur : les six illustrations vont du carré
  // (illu, 431x439) au portrait étroit (producteur, 138x291). Une largeur imposée étirerait ce
  // dernier sur près de 550 px de haut. Bornées en hauteur et laissées à leur largeur naturelle,
  // elles s'alignent toutes sur une même ligne de base.
  illustration: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 200,
    margin: '0 auto',

    [theme.breakpoints.up('md')]: {
      maxHeight: 240,
    },
  },
  // Le numéro vit dans le titre plutôt qu'à côté : l'intitulé annoncé porte alors le rang, sans
  // qu'il faille le répéter en texte pour les lecteurs d'écran.
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    marginBottom: '1rem',
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: 'clamp(1.15rem, 3vw, 1.5rem)',
    lineHeight: 1.25,
    color: theme.palette.client.primary.extraDark,
  },
  // Le rang vient de la position dans le tableau, jamais de la donnée : une numérotation
  // désynchronisée de l'ordre réel est ainsi impossible.
  rank: {
    flexShrink: 0,
    width: '2.25rem',
    height: '2.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: theme.palette.client.primary.main,
    color: '#fff',
    fontSize: '1rem',
  },
  // L'alternance ne s'applique qu'à partir de `md` : en dessous, l'illustration précède toujours
  // son texte, car une colonne unique n'a pas de côté.
  illustrationLast: {
    [theme.breakpoints.up('md')]: {
      order: 1,
    },
  },
}));

const EditorialJourney: React.FC<EditorialJourneyProps> = ({ steps }) => {
  const classes = useStyles();
  const theme = useTheme();

  // Sorties du rendu et non masquées : une image en `display: none` reste dans le DOM et peut être
  // téléchargée pour rien. Le seuil `md` est celui que `PublicHeader` emploie déjà pour départager
  // mobile et desktop dans cette application.
  const showIllustrations = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <div className={classes.journey}>
      {steps.map((step, index) => {
        const rank = index + 1;
        const isIllustrationLast = index % 2 === 1;

        return (
          <Grid container spacing={3} alignItems="center" key={step.title}>
            {showIllustrations && (
              <Grid item md={5} className={isIllustrationLast ? classes.illustrationLast : ''}>
                <img
                  className={classes.illustration}
                  src={step.illustration}
                  alt={step.illustrationAlt}
                  loading="lazy"
                />
              </Grid>
            )}

            <Grid item xs={12} md={7}>
              <Typography component="h3" className={classes.title}>
                <span className={classes.rank}>{rank}</span>
                {step.title}
              </Typography>

              <EditorialBulletList items={step.points} />
            </Grid>
          </Grid>
        );
      })}
    </div>
  );
};

export default EditorialJourney;
